import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { getCategoryConfig, setCategoryVisibility, getPaymentMethodConfig, setPaymentMethodEnabled, getVehicleCategoryConfig, setVehicleCategoryAllowed } from '../api/admin';

// Country-Specific Home Pages plan — mirrors backend/src/routes/config.js
// DEFAULT_CATEGORIES exactly, used only so every checkbox has a determinate
// starting value before country_categories has been migrated/seeded.
const CATEGORY_DEFAULTS = {
  KE: { ride: true, carsharing: false, rent: true, list_vehicle: false, scooter: false, transit: false, hotels: true, vacation_rentals: true, wedding_halls: true, events: true, bodaboda: true },
  US: { ride: true, carsharing: true, rent: true, list_vehicle: true, scooter: true, transit: true, hotels: false, vacation_rentals: false, wedding_halls: false, events: false, bodaboda: false },
};

const CATEGORY_LABEL = {
  ride: 'Ride (with driver)',
  carsharing: 'Car sharing',
  rent: 'Rent a vehicle',
  list_vehicle: 'List your own vehicle for rent',
  scooter: 'Scooter rental',
  transit: 'Transit (bus, train)',
  hotels: 'Hotels',
  vacation_rentals: 'Vacation rentals',
  wedding_halls: 'Wedding halls',
  events: 'Events',
  bodaboda: 'Boda boda',
};

// Registration & Multi-Role Concept plan, Section 1 — mirrors
// backend/src/routes/config.js DEFAULT_PAYMENT_METHODS exactly.
const PAYMENT_DEFAULTS = {
  KE: { mobile_money: true, card: true },
  US: { mobile_money: false, card: true },
};

const PAYMENT_LABEL = {
  mobile_money: 'Mobile Money (e.g. M-Pesa)',
  card: 'Credit / debit card',
};

// Vehicle Category Rules (Sept 2026) — replaces the old flat "Cars Only"
// constant. Mirrors backend/src/lib/vehicleCategories.js DEFAULT_ALLOWED
// exactly. Motorcycle/boda-boda are mapped onto the existing 'bike'/'scooter'
// values (see that file's comment for why).
const VEHICLE_CATEGORY_DEFAULTS = {
  US: { economy: true, sedan: true, suv: true, luxury: true, hatchback: true, station_wagon: true, minivan: true, van: true, truck: false, scooter: false, bike: false, transit: false },
  KE: { economy: true, sedan: true, suv: true, luxury: true, hatchback: true, station_wagon: true, minivan: true, van: true, truck: false, scooter: true, bike: true, transit: false },
};

const VEHICLE_CATEGORY_LABEL = {
  economy: 'Economy car', sedan: 'Sedan', suv: 'SUV', luxury: 'Luxury car',
  hatchback: 'Hatchback', station_wagon: 'Station wagon', minivan: 'Minivan',
  van: 'Passenger van / minibus', truck: 'Truck',
  scooter: 'Boda-boda / scooter (motorcycle)', bike: 'Motorcycle',
  transit: 'Transit vehicle',
};

// Same 6 markets the public site's MarketContext supports (public-website/src/lib/markets.js)
// — kept as a local copy since admin-dashboard is a separate app with no shared imports.
const COUNTRY_NAMES = {
  KE: 'Kenya', US: 'United States', DE: 'Germany', GB: 'United Kingdom', FR: 'France', ES: 'Spain',
};

// Real flag images (flagcdn.com), not emoji — Windows doesn't reliably render
// Unicode flag emoji as actual pictures, same issue already hit and fixed on
// the public site's country switcher.
const CountryHeader = ({ code }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
    <img
      src={`https://flagcdn.com/${code.toLowerCase()}.svg`}
      alt=""
      style={{ width: 18, height: 13, objectFit: 'cover', borderRadius: 2, boxShadow: '0 0 0 1px rgba(0,0,0,0.08)' }}
    />
    {COUNTRY_NAMES[code] || code}
  </span>
);

// Shared toggle-grid used for both the category table and the payment
// method table below it — same country columns, same checkbox behavior.
function ToggleGrid({ rowLabels, rowKeys, countries, values, savingKey, onToggle }) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 420, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12 }}>
        <thead>
          <tr>
            <th style={th}></th>
            {countries.map((c) => <th key={c} style={{ ...th, textAlign: 'center' }}><CountryHeader code={c} /></th>)}
          </tr>
        </thead>
        <tbody>
          {rowKeys.map((key) => (
            <tr key={key}>
              <td style={td}>{rowLabels[key] || key}</td>
              {countries.map((country) => {
                const cellKey = `${country}:${key}`;
                return (
                  <td key={country} style={{ ...td, textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={!!values[country]?.[key]}
                      disabled={savingKey === cellKey}
                      onChange={() => onToggle(country, key)}
                      style={{ width: 18, height: 18, cursor: 'pointer' }}
                    />
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function CategoryConfig() {
  const [countries, setCountries] = useState(['KE', 'US']);
  const [newCountry, setNewCountry] = useState('');

  const [categoryRows, setCategoryRows] = useState([]);
  const [categoryKeys, setCategoryKeys] = useState(Object.keys(CATEGORY_LABEL));
  const [categoryLoading, setCategoryLoading] = useState(true);
  const [categorySavingKey, setCategorySavingKey] = useState(null);

  const [paymentRows, setPaymentRows] = useState([]);
  const [paymentKeys, setPaymentKeys] = useState(Object.keys(PAYMENT_LABEL));
  const [paymentLoading, setPaymentLoading] = useState(true);
  const [paymentSavingKey, setPaymentSavingKey] = useState(null);

  const [vehicleCategoryRows, setVehicleCategoryRows] = useState([]);
  const [vehicleCategoryKeys, setVehicleCategoryKeys] = useState(Object.keys(VEHICLE_CATEGORY_LABEL));
  const [vehicleCategoryLoading, setVehicleCategoryLoading] = useState(true);
  const [vehicleCategorySavingKey, setVehicleCategorySavingKey] = useState(null);

  const [error, setError] = useState('');

  const loadCategories = async () => {
    setCategoryLoading(true);
    try {
      const res = await getCategoryConfig();
      setCategoryRows(res.data.categories || []);
      if (res.data.category_keys?.length) setCategoryKeys(res.data.category_keys);
      const extra = [...new Set((res.data.categories || []).map((r) => r.country_code))];
      setCountries((prev) => [...new Set([...prev, ...extra])]);
    } catch (e) {
      setError(e.response?.data?.error || 'Could not load category config.');
    } finally {
      setCategoryLoading(false);
    }
  };

  const loadPaymentMethods = async () => {
    setPaymentLoading(true);
    try {
      const res = await getPaymentMethodConfig();
      setPaymentRows(res.data.methods || []);
      if (res.data.method_keys?.length) setPaymentKeys(res.data.method_keys);
      const extra = [...new Set((res.data.methods || []).map((r) => r.country_code))];
      setCountries((prev) => [...new Set([...prev, ...extra])]);
    } catch (e) {
      setError(e.response?.data?.error || 'Could not load payment method config.');
    } finally {
      setPaymentLoading(false);
    }
  };

  const loadVehicleCategories = async () => {
    setVehicleCategoryLoading(true);
    try {
      const res = await getVehicleCategoryConfig();
      setVehicleCategoryRows(res.data.categories || []);
      if (res.data.category_keys?.length) setVehicleCategoryKeys(res.data.category_keys);
      const extra = [...new Set((res.data.categories || []).map((r) => r.country_code))];
      setCountries((prev) => [...new Set([...prev, ...extra])]);
    } catch (e) {
      setError(e.response?.data?.error || 'Could not load vehicle category config.');
    } finally {
      setVehicleCategoryLoading(false);
    }
  };

  useEffect(() => { loadCategories(); loadPaymentMethods(); loadVehicleCategories(); }, []);

  const categoryValues = useMemo(() => {
    const map = {};
    for (const country of countries) map[country] = { ...(CATEGORY_DEFAULTS[country] || CATEGORY_DEFAULTS.US) };
    for (const row of categoryRows) {
      if (!map[row.country_code]) map[row.country_code] = { ...(CATEGORY_DEFAULTS[row.country_code] || CATEGORY_DEFAULTS.US) };
      map[row.country_code][row.category_key] = row.is_visible;
    }
    return map;
  }, [categoryRows, countries]);

  const paymentValues = useMemo(() => {
    const map = {};
    for (const country of countries) map[country] = { ...(PAYMENT_DEFAULTS[country] || PAYMENT_DEFAULTS.US) };
    for (const row of paymentRows) {
      if (!map[row.country_code]) map[row.country_code] = { ...(PAYMENT_DEFAULTS[row.country_code] || PAYMENT_DEFAULTS.US) };
      map[row.country_code][row.method_key] = row.is_enabled;
    }
    return map;
  }, [paymentRows, countries]);

  const vehicleCategoryValues = useMemo(() => {
    const map = {};
    for (const country of countries) map[country] = { ...(VEHICLE_CATEGORY_DEFAULTS[country] || VEHICLE_CATEGORY_DEFAULTS.US) };
    for (const row of vehicleCategoryRows) {
      if (!map[row.country_code]) map[row.country_code] = { ...(VEHICLE_CATEGORY_DEFAULTS[row.country_code] || VEHICLE_CATEGORY_DEFAULTS.US) };
      map[row.country_code][row.category_key] = row.is_allowed;
    }
    return map;
  }, [vehicleCategoryRows, countries]);

  const toggleCategory = async (country, key) => {
    const next = !categoryValues[country]?.[key];
    const cellKey = `${country}:${key}`;
    setCategorySavingKey(cellKey);
    try {
      const res = await setCategoryVisibility(country, key, next);
      setCategoryRows((prev) => {
        const idx = prev.findIndex((r) => r.country_code === country && r.category_key === key);
        if (idx === -1) return [...prev, res.data.category];
        const copy = [...prev];
        copy[idx] = res.data.category;
        return copy;
      });
    } catch (e) {
      toast.error(e.response?.data?.error || 'Could not update category');
    } finally {
      setCategorySavingKey(null);
    }
  };

  const togglePayment = async (country, key) => {
    const next = !paymentValues[country]?.[key];
    const cellKey = `${country}:${key}`;
    setPaymentSavingKey(cellKey);
    try {
      const res = await setPaymentMethodEnabled(country, key, next);
      setPaymentRows((prev) => {
        const idx = prev.findIndex((r) => r.country_code === country && r.method_key === key);
        if (idx === -1) return [...prev, res.data.method];
        const copy = [...prev];
        copy[idx] = res.data.method;
        return copy;
      });
    } catch (e) {
      toast.error(e.response?.data?.error || 'Could not update payment method');
    } finally {
      setPaymentSavingKey(null);
    }
  };

  const toggleVehicleCategory = async (country, key) => {
    const next = !vehicleCategoryValues[country]?.[key];
    const cellKey = `${country}:${key}`;
    setVehicleCategorySavingKey(cellKey);
    try {
      const res = await setVehicleCategoryAllowed(country, key, next);
      setVehicleCategoryRows((prev) => {
        const idx = prev.findIndex((r) => r.country_code === country && r.category_key === key);
        if (idx === -1) return [...prev, res.data.category];
        const copy = [...prev];
        copy[idx] = res.data.category;
        return copy;
      });
    } catch (e) {
      toast.error(e.response?.data?.error || 'Could not update vehicle category');
    } finally {
      setVehicleCategorySavingKey(null);
    }
  };

  const addCountry = () => {
    const code = newCountry.trim().toUpperCase();
    if (!code || countries.includes(code)) return;
    setCountries((prev) => [...prev, code]);
    setNewCountry('');
  };

  return (
    <div style={{ padding: 24 }}>
      <h2 style={{ margin: '0 0 6px', fontSize: 22, fontWeight: 800 }}>Home Page Categories</h2>
      <p style={{ margin: '0 0 24px', color: '#64748b', fontSize: 14 }}>
        Which category tiles show on the home page, per country. No code changes needed — takes effect immediately.
      </p>

      {error && <p style={{ color: '#b91c1c' }}>{error}</p>}
      {categoryLoading ? <p>Loading…</p> : (
        <ToggleGrid rowLabels={CATEGORY_LABEL} rowKeys={categoryKeys} countries={countries} values={categoryValues} savingKey={categorySavingKey} onToggle={toggleCategory} />
      )}

      <h2 style={{ margin: '32px 0 6px', fontSize: 22, fontWeight: 800 }}>Payment Methods</h2>
      <p style={{ margin: '0 0 16px', color: '#64748b', fontSize: 14 }}>
        Which payment method options are offered per country. Note: this only controls which options are shown — Mobile Money still needs its own payment processing integration before it can actually take a payment.
      </p>
      {paymentLoading ? <p>Loading…</p> : (
        <ToggleGrid rowLabels={PAYMENT_LABEL} rowKeys={paymentKeys} countries={countries} values={paymentValues} savingKey={paymentSavingKey} onToggle={togglePayment} />
      )}

      <h2 style={{ margin: '32px 0 6px', fontSize: 22, fontWeight: 800 }}>Vehicle Category Rules</h2>
      <p style={{ margin: '0 0 16px', color: '#64748b', fontSize: 14 }}>
        Which vehicle body types a vehicle owner may list, per country — enforced both in the listing form and server-side (a direct API call can't bypass this). Passenger car types are capped at 9 seats including the driver.
      </p>
      {vehicleCategoryLoading ? <p>Loading…</p> : (
        <ToggleGrid rowLabels={VEHICLE_CATEGORY_LABEL} rowKeys={vehicleCategoryKeys} countries={countries} values={vehicleCategoryValues} savingKey={vehicleCategorySavingKey} onToggle={toggleVehicleCategory} />
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 20, alignItems: 'center' }}>
        <input
          value={newCountry}
          onChange={(e) => setNewCountry(e.target.value)}
          placeholder="Add country code (e.g. DE)"
          style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 14, width: 220 }}
        />
        <button
          onClick={addCountry}
          style={{ padding: '8px 14px', borderRadius: 8, border: 'none', background: '#0B6A4A', color: '#fff', fontWeight: 600, cursor: 'pointer', fontSize: 13 }}
        >
          Add country
        </button>
      </div>
    </div>
  );
}

const th = { textAlign: 'left', padding: '10px 14px', borderBottom: '2px solid #e2e8f0', fontSize: 13, color: '#334155', fontWeight: 700 };
const td = { padding: '10px 14px', borderBottom: '1px solid #f1f5f9', fontSize: 14, color: '#0f172a' };
